import { observable, action, runInAction } from 'mobx';
import { RootStore, Customer } from '..';
import { fetchWrapper } from '../../common/Fetch';
import { NotificationLevel } from '../NotificationStore';
import { SuccessableResult } from '../../common/BaseClasses';
import { v4 as uuidv4 } from 'uuid';

export interface DeliveryPoint {
    id: number;
    name: string;
    fullName: string;
    description: string;
}


export interface Article {
    articleId: number;
    brandGroupId: number;
    brand: string;
    article: string;
    description: string;
    fromTecdoc: boolean;
    fromLaximo: boolean;
}

export interface Part extends Article {
    supTimeMin: number;
    supTimeMax: number;
    deliveryTimeString: string;
    supName: string;
    supNameForOrder: string;
    supCode: string;
    supId: number;
    cost: number;
    count: number;
    minQuantity: number;
    lotQuantity: number;
    color: string;
    toOrderCount: number;
    isAllowDiscountRefund: boolean;
    refundDiscount: number;
    branchSort: number;
    hasBranchParts: boolean;
    isAnalog: boolean;
    sorting: number;
    brandUrl: string;
    timeToRegionMin: number;
    timeToRegionMax: number;
    timeToRegionString: string;
}

export interface VerifiedArticles {
    original: number;
    analogs: Analog[];
}

export interface Analog {
    articleId: number;
    brandGroupId: number;
    status: number;
}

export interface PartsGroupInfo {
    items: Part[];
    totalCount: number;
    skip: number;
    take: number;
    manyOffersByBranch: number[];
    // minPrice: number;
}

export interface ArticleCross {
    id: number;
    article: string;
    brand: string;
    description: string;
}

export interface CrossView {
    hasSimple: boolean;
    fromLaximo: boolean;
    simple: ArticleCross[];
    complex: ArticleCross[];
    details: ArticleCross[];
}

interface DraftOrderDetails {
    customerId: string;
    customerName: string;
    managerName: string;
    deliveryPointId?: number;
    positions: CreateOrderPosition[];
    source: string;
    comment: string;
    commentForClient: string;
    vinQueryIdAnswer?: number;
    isAnsweredByVinQuery: boolean;
    customerManagerName: string;
    customerManagerPhone: string;
    isAllowDiscountRefund: boolean;
    refundDiscount: number;
}

interface UpdatePositionResult {
    position: CreateOrderPosition;
    isNotFound: boolean;
    remmants: number;
}

export interface CreateOrderPosition {
    sorting: number;
    comment: string;
    uuid: string;
    id: number;
    brand: string;
    article: string;
    description: string;
    count: number;
    awailableCount: number;
    costSale: number;
    newCostSale: number;
    supCode: string;
    supName: string;
    supNameForOrder: string;
    supTimeMin: number;
    supTimeMax: number;
    deliveryTimeString: string;
    isInUpdating: boolean;
    relevancyStatus: string;
    notFound: boolean;
    priceChangedUp: boolean;
    priceChangedDown: boolean;
    isNotActually: boolean;
    isChecked: boolean;
    vinQueryPositionId?: number;
    isAllowDiscountRefund: boolean;
    refundDiscount: number;
    branchSort: number;
    hasBranchParts: boolean;
    timeToRegionMin: number;
    timeToRegionMax: number;
    timeToRegionString: string;
}

interface CreateOrderResult {
    unsuccessfulPositions: string[]
}

export interface RussianCarPartItem {
    brandName: string;
    brandId: number;
    article: string;
    description: string;
    count: number;
    toOrderCount: number;
    price: number;
    supNameForOrder: string;
    supplierCode: string;
    supTimeMin: number;
    supTimeMax: number;
    deliveryTimeString: string;
}

export interface RussianCarPartBrandInfo {
    brandId: number;
    brandGroupId: number;
    brandName: string;
}

interface SearchRussianCarPartsResult {
    searchId: string;
    positions: RussianCarPartItem[];
    totalCount: number;
    page: number;
    brands: RussianCarPartBrandInfo[];
    letters: string[]
}

export interface CreateOrderCdekSettings {
    toAddress: boolean;
    receiverCityId: number;
    terminalCode?: string;
    street?: string;
    building?: string;
    appartments?: string;
    address?: string;
}

export class CreateEditCustomerOrderStore {
    _rootStore: RootStore;

    @observable positions: any[] = [];
    @observable articles: Article[] = [];
    @observable parts: Part[] = [];
    // @observable customers: Customer[] = []
    @observable deliveryPoints: DeliveryPoint[] = [];
    @observable searchKey = "";

    @observable whatWant: PartsGroupInfo = { items: [], skip: 0, take: 7, totalCount: 0, manyOffersByBranch: [] };
    @observable originals: PartsGroupInfo = { items: [], skip: 0, take: 2, totalCount: 0, manyOffersByBranch: [] };
    @observable analogues: PartsGroupInfo = { items: [], skip: 0, take: 100, totalCount: 0, manyOffersByBranch: [] };

    @observable verifiedArticles: VerifiedArticles = { original: 0, analogs: [] }
    @observable crosses: CrossView | null = null;
    @observable deletedCrosses = observable.set<string>();
    constructor(rootStore: RootStore) {
        this._rootStore = rootStore;
    }

    @action
    searchRussianCarParts(searchId: string, clientId: string, source: string, searchString: string, brandId: number | undefined, fristCharacter: string,
        successCallback: (positions: RussianCarPartItem[], searchId: string, page: number, totalCount: number, brands: RussianCarPartBrandInfo[], letters: string[]) => void) {
        fetchWrapper(`api/SearchProducts/getrussian/${clientId}?searchId=${searchId}&source=${source}&brandId=${brandId || ""}&fristCharacter=${fristCharacter}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        }, this._rootStore.commonStore)
            .then(res => res.json() as Promise<SearchRussianCarPartsResult>)
            .then(result => {
                runInAction("fetch complete loadDraftOrder", () => {
                    result.positions.forEach(w => w.toOrderCount = 1);
                    successCallback(
                        result.positions,
                        result.searchId,
                        result.page,
                        result.totalCount,
                        result.brands,
                        result.letters);
                });
            })
            .catch(res => {
                console.log(res);
            });
    }

    @action
    loadDraftOrderDetails(draftOrderId: number, callback: (clientId: string, customerName: string, managerName: string, deliveryPointId: number, source: string, comment: string, commentForClient: string, customerManagerName: string, customerManagerPhone: string, positions: CreateOrderPosition[], vinQueryId?: number) => void) {
        fetchWrapper('api/CustomerOrders/draftdetails/' + draftOrderId, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        }, this._rootStore.commonStore)
            .then(res => res.json() as Promise<DraftOrderDetails>)
            .then(result => {
                runInAction("fetch complete loadDraftOrder", () => {
                    const i = 1;
                    callback(
                        result.customerId,
                        result.customerName,
                        result.managerName,
                        result.deliveryPointId || -1,
                        result.source,
                        result.comment,
                        result.commentForClient,
                        result.customerManagerName,
                        result.customerManagerPhone,
                        result.positions.sort((a, b) => a.sorting > b.sorting ? 1 : -1).map((w, index) => { w.uuid = uuidv4(); w.sorting = index + 1; return w; }),
                        result.vinQueryIdAnswer);
                });
            })
            .catch(res => {
                console.log(res);
            });
    }

    @action
    updatePosition(draftOrderId: number, positionId: number, callback: (position: CreateOrderPosition, isNotFound: boolean, remmants: number) => void) {
        fetchWrapper(`api/CustomerOrders/UpdateOrderPosition/${draftOrderId}/${positionId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        }, this._rootStore.commonStore, false)
            .then(res => res.json() as Promise<UpdatePositionResult>)
            .then(result => {
                runInAction("fetch complete loadDraftOrder", () => {
                    result.position.uuid = uuidv4();
                    callback(result.position, result.isNotFound, result.remmants);
                });
            })
            .catch(res => {
                console.log(res);
            });
    }

    @action
    loadCustomers(callback: (customers: Customer[]) => void) {
        fetchWrapper('api/CustomerOrders/getcustomers', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        }, this._rootStore.commonStore)
            .then(res => res.json() as Promise<Customer[]>)
            .then(result => {
                runInAction("fetch complete loadCustomers", () => {
                    callback(result);
                });
            })
            .catch(res => {
                console.log(res);
            });
    }
    @action
    createOrder(formUniqueKey: number, positions: CreateOrderPosition[], clientId: string,
        source: string,
        deliveryType: "delivery" | "pickup",
        paymentType: "Cache" | "Cacheless" | "ByReceipt",
        isUrgent: boolean,
        deliveryPointId: number,
        comment: string,
        commentForClient: string,
        confirmOrder: boolean,
        sendPaymentLink: boolean,
        cdekSettings: CreateOrderCdekSettings | undefined,
        callback: (unsuccessedPositions: string[]) => void, isDraft = false, draftOrderId?: number, vinQueryId?: number, customerManagerName?: string, customerManagerPhone?: string) {

        const data = {
            clientId,
            DeliveryPointId: deliveryPointId == -1 ? undefined : deliveryPointId,
            source,
            positions,
            deliveryType: deliveryType == "delivery" ? 1 : 2,
            paymentType: paymentType == "Cache" ? 1 : paymentType == "Cacheless" ? 2 : 3,
            isUrgent,
            comment,
            commentForClient,
            formUniqueKey,
            confirmOrder,
            sendPaymentLink,
            cdekSettings,
            customerManagerName,
            customerManagerPhone
        };
        fetchWrapper((isDraft ? 'api/CustomerOrders/savedraft' : 'api/CustomerOrders') + '?draftOrderId=' + (draftOrderId || "-1") + '&vinQueryId=' + (vinQueryId || ""), {
            method: 'POST',
            body: JSON.stringify(data),
            headers: {
                'Content-Type': 'application/json'
            }
        }, this._rootStore.commonStore)
            .then(res => res.json() as Promise<SuccessableResult<CreateOrderResult>>)
            .then(result => {
                runInAction("fetch complete loadDeliveryPoints", () => {
                    if (result.isSuccess) {
                        let unsuccessedPositions: string[] = [];
                        if (result.errorMessage != undefined && !isDraft) {
                            this._rootStore.notificationStore.addNotification(result.errorMessage, NotificationLevel.warning, -1);
                        } else if (
                            !isDraft
                            && result.data != null
                            && result.data.unsuccessfulPositions != null
                            && result.data.unsuccessfulPositions.length > 0) {

                            unsuccessedPositions = result.data.unsuccessfulPositions;

                            const notOrderedPositions = result.data.unsuccessfulPositions.map(uuid => positions.filter(f => f.uuid == uuid)[0]);

                            const notOrderedPositionsText = notOrderedPositions.map(item => `${item.brand}:${item.article}`).join("\r\n")

                            this._rootStore.notificationStore.addNotification(`Заказ создан частично!\r\nНезаказанные позиции: \r\n${notOrderedPositionsText}`, NotificationLevel.warning, 0);
                        }
                        else
                            this._rootStore.notificationStore.addNotification(`${isDraft ? "Черновик" : "Заказ"} успешно создан!`, NotificationLevel.success);
                        callback(unsuccessedPositions);
                    }
                    else {
                        this._rootStore.notificationStore.addNotification("Ошибка при сохранении! " + result.errorMessage, NotificationLevel.error);
                    }
                });
            })
            .catch(res => {
                console.log(res);
            });
    }
    @action
    loadDeliveryPoints(source: string) {
        fetchWrapper('api/DeliveryPoints/all/' + source, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        }, this._rootStore.commonStore)
            .then(res => res.json() as Promise<DeliveryPoint[]>)
            .then(result => {
                runInAction("fetch complete loadDeliveryPoints", () => {
                    this.deliveryPoints = result;
                });
            })
            .catch(res => {
                console.log(res);
            });
    }

    @action
    searchByPair(brand: string, article: string, clientId: string, source: string, deliveryPointId: number, sortBy = "time") {
        this.articles = [];
        this.whatWant = { items: [], skip: 0, take: 7, totalCount: 0, manyOffersByBranch: [] };
        this.originals = { items: [], skip: 0, take: 2, totalCount: 0, manyOffersByBranch: [] };
        this.analogues = { items: [], skip: 0, take: 100, totalCount: 0, manyOffersByBranch: [] };
        article = encodeURIComponent(article);
        brand = encodeURIComponent(brand);

        fetchWrapper(`api/SearchProducts/getparts/${clientId}?source=${source}&article=${article}&brand=${brand}&deliveryPointId=${deliveryPointId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        }, this._rootStore.commonStore)
            .then(res => res.json() as Promise<SuccessableResult<string>>)
            .then(result =>
                runInAction("fetch complete searchByPair", () => {
                    if (result.isSuccess) {
                        this.searchKey = result.data;
                        this.getResultsByGroup(result.data, 1, sortBy, 0, 7);
                        this.getResultsByGroup(result.data, 2, sortBy, 0, 2);
                        this.getResultsByGroup(result.data, 3, sortBy, 0, 100);
                    }
                    else
                        this._rootStore.notificationStore.addNotification("Ошибка при поиске товара! " + result.errorMessage, NotificationLevel.error);
                })
            )
            .catch(res => {
                console.log(res);
            });
    }

    @action
    getVerifiedArticles(brandGroupId: number, articleId: number) {
        this.verifiedArticles = { original: articleId, analogs: [] };

        fetchWrapper(`api/SearchProducts/getverifiedarticles/${brandGroupId}/${articleId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        }, this._rootStore.commonStore)
            .then(res => res.json() as Promise<VerifiedArticles>)
            .then(result =>
                runInAction("fetch complete getVerifiedArticles", () => {
                    this.verifiedArticles = result;
                })
            )
            .catch(res => {
                console.log(res);
                this._rootStore.notificationStore.addNotification("Ошибка при поиске аналогов! " + res, NotificationLevel.error);
            });
    }

    @action
    verifyArticle(originalBrandGroupId: number, originalArticleId: number, analogueBrandGroupId: number, analogueArticleId: number, callback: () => void) {
        fetchWrapper('/api/searchproducts/verifyarticle', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                originalBrandGroupId,
                originalArticleId,
                analogueBrandGroupId,
                analogueArticleId
            })
        }, this._rootStore.commonStore, false)
            .then(res => {
                if (!res.ok) {
                    throw new Error(`Ошибка сервера: ${res.status} ${res.statusText}`);
                }
                return res.json();
            })
            .then(result => {
                runInAction("fetch complete verifyArticle", () => {
                    if (result.isSuccess) {
                        const idx = this.verifiedArticles.analogs.findIndex(a =>
                            a.articleId === analogueArticleId && a.brandGroupId === analogueBrandGroupId
                        );

                        if (idx >= 0) {
                            const prev = this.verifiedArticles.analogs[idx];
                            this.verifiedArticles.analogs[idx] = { ...prev, status: 1 };
                        } else {
                            this.verifiedArticles.analogs.push({
                                articleId: analogueArticleId,
                                brandGroupId: analogueBrandGroupId,
                                status: 1
                            });
                        }

                        this._rootStore.notificationStore.addNotification("Аналог успешно привязан", NotificationLevel.success);
                        callback();
                    } else {
                        this._rootStore.notificationStore.addNotification(`Ошибка: ${result.errorMessage}`, NotificationLevel.error);
                    }
                });
            })
            .catch(error => {
                runInAction("fetch error verifyArticle", () => {
                    this._rootStore.notificationStore.addNotification(`Ошибка при привязке аналога: ${error.message}`, NotificationLevel.error);
                });
            });
    }

    @action
    unverifyArticle(originalBrandGroupId: number, originalArticleId: number, analogueBrandGroupId: number, analogueArticleId: number, callback: () => void) {
        this.verifiedArticles.analogs = this.verifiedArticles.analogs.filter(
            a => a.articleId !== analogueArticleId
        );

        fetchWrapper('/api/searchproducts/unverifyarticle', {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                originalBrandGroupId,
                originalArticleId,
                analogueBrandGroupId,
                analogueArticleId
            })
        }, this._rootStore.commonStore, false)
            .then(res => {
                if (!res.ok) {
                    throw new Error(`Ошибка сервера: ${res.status} ${res.statusText}`);
                }
                return res.json();
            })
            .then(result => {
                runInAction("fetch complete unverifyArticle", () => {
                    if (result.isSuccess) {
                        this._rootStore.notificationStore.addNotification("Аналог успешно отвязан", NotificationLevel.success);
                        callback();
                    } else {
                        this._rootStore.notificationStore.addNotification(`Ошибка: ${result.errorMessage}`, NotificationLevel.error);
                    }
                });
            })
            .catch(error => {
                runInAction("fetch error unverifyArticle", () => {
                    this._rootStore.notificationStore.addNotification(`Ошибка при отвязке аналога: ${error.message}`, NotificationLevel.error);
                });
            });
    }

    @action
    getResultsByGroup(searchId: string, spareType: number, sortBy = "time", skip = 0, take = 100) {
        fetchWrapper(`api/SearchProducts/getresultsbygroup/${searchId}/${spareType}/${sortBy}/${skip}/${take}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        }, this._rootStore.commonStore)
            .then(res => res.json() as Promise<PartsGroupInfo>)
            .then(result =>
                runInAction("fetch complete getResultsByGroup", () => {
                    result.items.forEach(w => w.toOrderCount = Math.max(w.minQuantity, 1));

                    result.items.forEach(w => w.hasBranchParts = result.manyOffersByBranch.indexOf(w.sorting) > -1);

                    switch (spareType) {
                        case 1:
                            if (skip == 0)
                                this.whatWant = result;
                            else {
                                const items = this.whatWant.items;

                                this.whatWant = Object.assign({}, result, { items: [...items, ...result.items] });
                            }
                            break;
                        case 2:
                            if (skip == 0)
                                this.originals = result;
                            else {
                                const items = this.originals.items;
                                this.originals = Object.assign({}, result, { items: [...items, ...result.items] });
                            }
                            break;
                        case 3:
                            if (skip == 0)
                                this.analogues = result;
                            else {
                                const items = this.analogues.items;
                                this.analogues = Object.assign({}, result, { items: [...items, ...result.items] });
                            }
                            break;
                        default:
                            break;
                    }
                })
            )
            .catch(res => {
                console.log(res);
            });
    }

    @action
    getPartsByBranch(searchId: string, articleId: number) {

        return fetchWrapper(`api/SearchProducts/getresultsbyBranch/${searchId}/${articleId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        }, this._rootStore.commonStore)
            .then(res => res.json() as Promise<Part[]>)
            .then(result => {
                result.forEach(w => w.toOrderCount = Math.max(w.minQuantity, 1));
                return result; 
            })
            .catch(res => {
                console.log(res);
                throw res; 
            });
    }

    @action
    searchByArticle(article: string) {

        if (article == null || article.trim() === '') {
            this._rootStore.notificationStore.addNotification("Введите артикул", NotificationLevel.error);
            return;
        }

        this.articles = [];
        article = encodeURIComponent(article);
        fetchWrapper('api/SearchProducts/getbrands?article=' + article, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        }, this._rootStore.commonStore)
            .then(res => res.json() as Promise<Article[]>)
            .then(result =>
                runInAction("fetch complete searchByArticle", () => {
                    this.articles = result
                })
            )
            .catch(res => {
                console.log(res);
            });
    }

    @action
    getCrosses(originalArticleId: number, analogueArticleId: number) {
        return fetchWrapper(
            '/api/SearchProducts/getcrosses',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    originalArticleId,
                    analogueArticleId
                })
            },
            this._rootStore.commonStore
        )
            .then(res => res.json() as Promise<CrossView>)
            .then(result => {
                runInAction("fetch complete getCrosses", () => {
                    this.crosses = result;
                });

                return result;
            });
    }

    @action
    closeCrosses() {
        this.crosses = null;
        this.deletedCrosses.clear();
    }

    @action
    deleteCross(firstId: number, secondId: number) {

        return fetchWrapper(
            '/api/SearchProducts/deletecross',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    firstId,
                    secondId
                })
            },
            this._rootStore.commonStore
        )
            .then(res => res.json() as Promise<boolean>)
            .then(result => {

                runInAction("fetch complete deleteCross", () => {

                    if (result) {
                        this._rootStore.notificationStore.addNotification(
                            "Связь удалена",
                            NotificationLevel.success
                        );

                        this.markCrossDeleted(firstId, secondId);
                    }
                    else {
                        this._rootStore.notificationStore.addNotification(
                            "Связь не найдена",
                            NotificationLevel.warning
                        );
                    }

                });

                return result;
            })
            .catch(error => {

                console.log(error);

                this._rootStore.notificationStore.addNotification(
                    "Ошибка при удалении связи",
                    NotificationLevel.error
                );

                return false;
            });
    }

    @action
    markCrossDeleted(firstId: number, secondId: number) {

        this.deletedCrosses.add(
            this.getCrossKey(firstId, secondId)
        );
    }

    isCrossDeleted(firstId: number, secondId: number) {
        return this.deletedCrosses.has(
            this.getCrossKey(firstId, secondId)
        );
    }

    @action
    changeAddToOrderCount(spareType: number, rowNum: number, newCount: number) {
        switch (spareType) {
            case 1:
                this.whatWant.items = this.whatWant.items.map((w, index) => {
                    if (index == rowNum) {
                        return Object.assign({}, w, { toOrderCount: newCount });
                    }
                    return w;
                });
                break;
            case 2:
                this.originals.items = this.originals.items.map((w, index) => {
                    if (index == rowNum) {
                        return Object.assign({}, w, { toOrderCount: newCount });
                    }
                    return w;
                });
                break;
            case 3:
                this.analogues.items = this.analogues.items.map((w, index) => {
                    if (index == rowNum) {
                        return Object.assign({}, w, { toOrderCount: newCount });
                    }
                    return w;
                });
                break;
            default:
                break;
        }
    }

    private getCrossKey(firstId: number, secondId: number): string {
        return [firstId, secondId]
            .sort((a, b) => a - b)
            .join("_");
    }

}